import { motion } from 'framer-motion'
import { Users, Workflow, LayoutGrid, Check } from 'lucide-react'

type Tier = {
    eyebrow: string
    title: string
    description: string
    icon: React.ComponentType<{ className?: string }>
    includes: string[]
    replaces: string
}

const tiers: Tier[] = [
    {
        eyebrow: 'The workforce',
        title: 'A team of 100+ workers',
        description:
            'SDRs, content producers, ad ops, researchers, browser automators — hire one or build a full department.',
        icon: Users,
        includes: [
            'Roles across Sales, Marketing, Operations, Platform',
            'Department-level coordination out of the box',
            'New worker types added monthly',
        ],
        replaces: 'Replaces $30K+/mo of W-2 headcount',
    },
    {
        eyebrow: 'The execution layer',
        title: 'Autonomous workflows',
        description:
            'Workers run on schedules and triggers — sourcing leads, posting content, managing DMs, launching ads — in parallel.',
        icon: Workflow,
        includes: [
            'Cron and event-based scheduling',
            'Live activity feed with anomaly detection',
            'One-click approvals for high-impact actions',
        ],
        replaces: 'Replaces an ops manager + a project tool',
    },
    {
        eyebrow: 'The control plane',
        title: 'One unified platform',
        description:
            'CRM, content studio, ads manager, and conversation inbox in one place — across every channel and every company you run.',
        icon: LayoutGrid,
        includes: [
            'Native to Meta, LinkedIn, Gmail, HubSpot, Stripe & more',
            'Multi-company workspaces — one workforce, many brands',
            'Single dashboard for cost, output, and pipeline',
        ],
        replaces: 'Replaces a stack of 10+ SaaS subscriptions',
    },
]

export function WhatYouGet() {
    return (
        <section
            id="what-you-get"
            className="py-20 px-6 md:px-12 lg:px-16"
        >
            <div className="w-full max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center mb-14"
                >
                    <span className="inline-block font-body text-xs uppercase tracking-widest text-muted-foreground border border-border rounded-full px-4 py-1.5 mb-6">
                        What you get
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
                        A workforce, an execution layer,<br />and a single control plane.
                    </h2>
                    <p className="font-body text-lg text-muted-foreground font-light max-w-2xl mx-auto">
                        Three things in one engagement. Monthly salary. Included work budget. Output guaranteed.
                    </p>
                </motion.div>

                {/* Tier grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
                    {tiers.map((tier, index) => (
                        <TierCard key={tier.title} tier={tier} index={index} />
                    ))}
                </div>
            </div>
        </section>
    )
}

function TierCard({ tier, index }: { tier: Tier; index: number }) {
    const Icon = tier.icon
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            className="rounded-2xl border border-border bg-card p-7 flex flex-col gap-5 hover:border-primary/40 transition-colors duration-300"
        >
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl border border-border bg-background text-primary flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                </div>
                <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                    {tier.eyebrow}
                </span>
            </div>

            <h3 className="font-heading text-xl text-foreground leading-snug">
                {tier.title}
            </h3>

            <p className="font-body text-sm text-muted-foreground font-light">
                {tier.description}
            </p>

            <ul className="space-y-2 mt-auto">
                {tier.includes.map((line) => (
                    <li
                        key={line}
                        className="flex items-start gap-2.5 font-body text-sm text-foreground/90"
                    >
                        <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>{line}</span>
                    </li>
                ))}
            </ul>

            <div className="pt-4 border-t border-border/50">
                <p className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                    {tier.replaces}
                </p>
            </div>
        </motion.div>
    )
}
